import React, { useEffect, useState } from "react";
import { Eye, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Panel, StatusBadge } from "../lib/ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

export default function QC() {
  const { can } = useAuth();
  const [list, setList] = useState([]);
  const [detail, setDetail] = useState(null);

  const load = () => api.get("/qc/batches").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const openDetail = (id) => api.get(`/qc/batches/${id}`).then((r) => setDetail(r.data));

  return (
    <div data-testid="qc-page">
      <PageHeader title="Quality Control" subtitle="QC per varian: passed, rework, reject" />
      <Panel>
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Batch QC</TableHead><TableHead>Dari Penerimaan</TableHead><TableHead>Supplier</TableHead>
              <TableHead>Gudang</TableHead><TableHead className="text-center">Item</TableHead>
              <TableHead>Status</TableHead><TableHead>Dibuat</TableHead><TableHead className="text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.map((b) => (
              <TableRow key={b.id} data-testid={`qc-row-${b.id}`}>
                <TableCell className="font-mono text-xs font-semibold text-cocoa">{b.number} {b.is_rework && <span className="text-status-rework">(Rework)</span>}</TableCell>
                <TableCell className="font-mono text-xs">{b.receiving_number}</TableCell>
                <TableCell>{b.supplier}</TableCell>
                <TableCell>{b.warehouse_name}</TableCell>
                <TableCell className="text-center">{b.items?.length}</TableCell>
                <TableCell><StatusBadge status={b.status} /></TableCell>
                <TableCell className="text-xs text-wmuted">{fmtDate(b.created_at)}</TableCell>
                <TableCell className="text-right"><Button size="sm" variant="ghost" onClick={() => openDetail(b.id)} data-testid={`view-qc-${b.id}`}><Eye className="w-4 h-4" /></Button></TableCell>
              </TableRow>
            ))}
            {!list.length && <TableRow><TableCell colSpan={8} className="text-center text-wmuted py-10">Belum ada batch QC. Konfirmasi penerimaan untuk membuat batch.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Panel>

      {detail && <QCDialog detail={detail} setDetail={setDetail} can={can} reload={() => { load(); }} />}
    </div>
  );
}

function QCDialog({ detail, setDetail, can, reload }) {
  const [rows, setRows] = useState(detail.items.map((it) => ({
    item_id: it.id, qty_inspected: it.qty_inspected || 0, qty_passed: it.qty_passed || 0,
    qty_rework: it.qty_rework || 0, qty_reject: it.qty_reject || 0,
    defect_type: it.defect_type || "", qc_notes: it.qc_notes || "",
    meta: it,
  })));
  const canQC = can("qc_team", "supervisor");

  const upd = (i, k, v) => { const n = [...rows]; n[i][k] = k.startsWith("qty") ? (parseInt(v) || 0) : v; setRows(n); };

  const submit = async () => {
    // client validation
    for (const r of rows) {
      if (r.qty_passed + r.qty_rework + r.qty_reject !== r.qty_inspected) {
        return toast.error(`${r.meta.product_name}: Passed+Rework+Reject harus = Diperiksa`);
      }
      if (r.qty_inspected > r.meta.qty_received) return toast.error(`${r.meta.product_name}: Diperiksa > Diterima`);
    }
    try {
      await api.post(`/qc/batches/${detail.id}/inspect`, rows.map(({ meta, ...r }) => r));
      toast.success("Hasil QC disimpan"); setDetail(null); reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const requeueRework = async (itemId) => {
    try { const r = await api.post(`/qc/batches/${detail.id}/rework/${itemId}`); toast.success(`Batch rework dibuat: ${r.data.qc_batch}`); setDetail(null); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open onOpenChange={() => setDetail(null)}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading text-cocoa flex items-center gap-3">{detail.number} <StatusBadge status={detail.status} /></DialogTitle></DialogHeader>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Produk</TableHead><TableHead className="text-right">Diterima</TableHead>
                <TableHead className="w-20">Periksa</TableHead><TableHead className="w-20">Passed</TableHead>
                <TableHead className="w-20">Rework</TableHead><TableHead className="w-20">Reject</TableHead>
                <TableHead>Cacat</TableHead><TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r, i) => (
                <TableRow key={r.item_id}>
                  <TableCell className="font-medium text-cocoa text-sm">{r.meta.product_name}<div className="text-xs text-wmuted">{r.meta.color}/{r.meta.size}</div></TableCell>
                  <TableCell className="text-right font-semibold">{r.meta.qty_received}</TableCell>
                  <TableCell><Input type="number" disabled={!canQC} value={r.qty_inspected} onChange={(e) => upd(i, "qty_inspected", e.target.value)} className="h-8" data-testid={`qc-inspected-${i}`} /></TableCell>
                  <TableCell><Input type="number" disabled={!canQC} value={r.qty_passed} onChange={(e) => upd(i, "qty_passed", e.target.value)} className="h-8" data-testid={`qc-passed-${i}`} /></TableCell>
                  <TableCell><Input type="number" disabled={!canQC} value={r.qty_rework} onChange={(e) => upd(i, "qty_rework", e.target.value)} className="h-8" data-testid={`qc-rework-${i}`} /></TableCell>
                  <TableCell><Input type="number" disabled={!canQC} value={r.qty_reject} onChange={(e) => upd(i, "qty_reject", e.target.value)} className="h-8" data-testid={`qc-reject-${i}`} /></TableCell>
                  <TableCell><Input disabled={!canQC} value={r.defect_type} onChange={(e) => upd(i, "defect_type", e.target.value)} className="h-8" placeholder="jenis" /></TableCell>
                  <TableCell className="text-right">
                    {r.meta.qty_rework > 0 && canQC && <Button size="sm" variant="ghost" onClick={() => requeueRework(r.item_id)} title="Re-QC rework"><RefreshCcw className="w-4 h-4 text-status-rework" /></Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {canQC && (
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetail(null)} className="border-taupe text-cocoa">Tutup</Button>
            <Button onClick={submit} data-testid="submit-qc-btn" className="bg-cocoa hover:bg-cocoa-dark text-ivory">Simpan Hasil QC</Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
